import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FETCH_DATA_TAG_REPORTS,
  FETCH_DATA_TAG_REPORT_CONTENT,
  CLEAR_DATA_TAG_SNACKBAR,
} from "./DataTagConstants";
import "../../components/views/GenerateReport/Report.scss";
import Page_Loader from "../../assets/vizr_images/page_loader.gif";
import { Button, Input, makeStyles } from "@material-ui/core";
import CloseIcon from "../../assets/vizr_images/ic_remove.svg";
import SearchIcon from "../../assets/vizr_images/ic_search.svg";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { errorSnackbar } from "../../components/common/utilities";
import EnhancedTable from "../../components/shared/Table/Table";

const useStyles = makeStyles((theme) => ({
  reportSearchInput: {
    width: "350px",

    "& input": {
      padding: "5px 0px 5px 16px",

      "&::placeholder": {
        color: "#081981 !important",
        opacity: 1,
        fontSize: "16px",
      },
    },
  },

  reportActionButton: {
    cursor: "pointer",
    padding: "6px",
    margin: "0 5px",
    borderRadius: "50%",
    color: "#081981",
    backgroundColor: "#f1f4ff",
    transition: "all 0.2s ease",
    fontSize: "22px",

    "&:hover": {
      backgroundColor: "#081981",
      color: "#fff",
    },

    "&.disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
      pointerEvents: "none",
    },
  },
}));

const toTitleCase = (value = "") =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

function DataTagReports({ retport_type = "dataTagReport" }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [searchReportName, setSearchReportName] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [periodType, setPeriodType] = useState("");

  const reportsResponse = useSelector(
    (state) => state.DataTagReportsReducer.allReports,
  );

  const dataTagReports = reportsResponse?.reports || [];
  const totalReportsCount = reportsResponse?.totalElements || 0;

  const loader = useSelector((state) => state.DataTagReportsReducer.loader);

  const snackBarMessage = useSelector(
    (state) => state.DataTagReportsReducer.snackBarMessage,
  );

  const contentAction = useSelector(
    (state) => state.DataTagReportsReducer.contentAction,
  );

  useEffect(() => {
    dispatch({
      type: FETCH_DATA_TAG_REPORTS,
      payload: {
        reportType: retport_type,
        periodType: periodType,
        page: currentPage,
        size: recordsPerPage,
        search: appliedSearch,
      },
    });

    return () => {
      setOpenSnackBar(false);
      dispatch({ type: CLEAR_DATA_TAG_SNACKBAR });
    };
  }, [
    retport_type,
    periodType,
    currentPage,
    recordsPerPage,
    appliedSearch,
    dispatch,
  ]);

  useEffect(() => {
    if (snackBarMessage && snackBarMessage.status && snackBarMessage.message) {
      setOpenSnackBar(true);
    }
  }, [snackBarMessage]);

  const handlePopUpClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnackBar(false);
    dispatch({ type: CLEAR_DATA_TAG_SNACKBAR });
  };

  const handlePeriodTypeFilter = (type) => {
    setPeriodType((currentType) => (currentType === type ? "" : type));
    setCurrentPage(0);
  };

  const handleChangeRowsPerPage = (value) => {
    setCurrentPage(0);
    setRecordsPerPage(value);
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const renderNull = () => {
    return <>-</>;
  };

  const renderPeriod = (params) => {
    if (!params || !params.periodDisplayRange) {
      return renderNull();
    }

    return (
      <div className="report_titles" title={params.periodDisplayRange}>
        {params.periodDisplayRange}
      </div>
    );
  };

  const renderPeriodType = (params) => {
    if (!params || !params.periodType) {
      return renderNull();
    }

    return (
      <div className="report_titles">{toTitleCase(params.periodType)}</div>
    );
  };

  const renderTriggerType = (params) => {
    if (!params || !params.triggerType) {
      return renderNull();
    }

    return (
      <div className="report_titles">{toTitleCase(params.triggerType)}</div>
    );
  };

  const renderGeneratedBy = (params) => {
    if (!params || !params.generatedBy) {
      return renderNull();
    }

    return <div className="report_titles generateBy">{params.generatedBy}</div>;
  };

  const renderGeneratedAt = (params) => {
    if (!params || !params.generatedAt) {
      return renderNull();
    }

    const date = new Date(params.generatedAt);

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    const formattedDate = `${month}-${day}-${year}`;
    const formattedTime = `${hours}:${minutes}`;

    return (
      <div>
        <div className="report_titles">{formattedDate}</div>
        <div className="report_titles">{formattedTime}</div>
      </div>
    );
  };

  const getPreviewTitle = (report) => {
    const type = toTitleCase(report?.periodType || "Report");

    return `Druvstar - Data Security & Governance Report (${type})`;
  };

  const handlePreviewReport = (report) => {
    dispatch({
      type: FETCH_DATA_TAG_REPORT_CONTENT,
      payload: {
        reportId: report.id,
        mode: "preview",
        report,
        previewTitle: getPreviewTitle(report),
      },
    });
  };

  const renderActions = (params) => {
    if (!params || !params.id) {
      return renderNull();
    }

    const isRowBusy = contentAction?.reportId === params.id;

    return (
      <div className="side-icons report-actions">
        <OpenInNewIcon
          className={`report-action-icon ${
            classes.reportActionButton
          } ${isRowBusy ? "disabled" : ""}`}
          titleAccess="Preview Report"
          onClick={() => !isRowBusy && handlePreviewReport(params)}
        />
      </div>
    );
  };

  const handleChange = (e) => {
    setSearchReportName(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    setAppliedSearch(searchReportName);
  };

  const handleClearSearch = () => {
    setSearchReportName("");
    setAppliedSearch("");
    setCurrentPage(0);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const renderSearchBar = () => {
    return (
      <div className="repositorySearch">
        <div className="reportSearchBar">
          <Input
            value={searchReportName}
            type="text"
            placeholder="Search here..."
            onKeyPress={handleKeyPress}
            disableUnderline
            className={classes.reportSearchInput}
            onChange={handleChange}
          />

          <Button
            className="searchCancelIcon"
            onClick={handleClearSearch}
            aria-label="close"
          >
            <img src={CloseIcon} alt="close" />
          </Button>

          <hr />

          <Button
            className="searchIcon"
            onClick={handleSearch}
            aria-label="search"
          >
            <img src={SearchIcon} alt="search" />
          </Button>
        </div>
      </div>
    );
  };

  const reportColumns = [
    {
      title: "Period",
      render: renderPeriod,
    },
    {
      title: "Frequency",
      render: renderPeriodType,
    },
    {
      title: "Generation Type",
      render: renderTriggerType,
    },
    {
      title: "Generated By",
      render: renderGeneratedBy,
    },
    {
      title: "Generated At",
      render: renderGeneratedAt,
      orderId: "generatedAt",
    },
    {
      title: "Actions",
      render: renderActions,
    },
  ];

  const renderTable = () => {
    return (
      <EnhancedTable
        cols={reportColumns}
        data={dataTagReports}
        totalCount={totalReportsCount}
        loc="dataTagReport"
        currentPage={currentPage}
        recordsPerPage={recordsPerPage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        onChangePage={handleChangePage}
      />
    );
  };

  const renderDataTagReportsTable = () => {
    return (
      <>
        <div className="previously-generated-reports-title">
          Previously Generated Reports
        </div>

        <div className="reports-table-wrapper">
          <div className="quick-filters">
            <span className="quick-filters-label">Quick Filters:</span>

            <button
              type="button"
              className={`quick-filter-tag ${
                periodType === "MONTHLY" ? "active" : ""
              }`}
              onClick={() => handlePeriodTypeFilter("MONTHLY")}
            >
              Monthly
            </button>

            <button
              type="button"
              className={`quick-filter-tag ${
                periodType === "WEEKLY" ? "active" : ""
              }`}
              onClick={() => handlePeriodTypeFilter("WEEKLY")}
            >
              Weekly
            </button>
          </div>

          <div className="custom-data-grid-main dataEstate-report">
            {renderTable()}
          </div>
        </div>
      </>
    );
  };

  const renderMessage = () => {
    return (
      <>
        {snackBarMessage &&
          snackBarMessage.status &&
          snackBarMessage.message &&
          errorSnackbar(
            openSnackBar,
            handlePopUpClose,
            snackBarMessage.message,
            snackBarMessage.status,
          )}
      </>
    );
  };

  return (
    <>
      {loader && (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source reports datatag-reports-loader"
        />
      )}

      {renderDataTagReportsTable()}

      {renderMessage()}
    </>
  );
}

export default DataTagReports;
